"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import type { Testimonial } from "@/lib/testimonials";

export function TestimonialsCarousel({ testimonials }: { testimonials: Testimonial[] }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % testimonials.length);
  }, [testimonials.length]);

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    if (paused || testimonials.length <= 1) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [paused, next, testimonials.length]);

  if (!testimonials.length) return null;

  const t = testimonials[current];

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="overflow-hidden">
        <div className="mx-auto max-w-3xl px-12">
          <div className={`flex flex-col items-center gap-8 sm:flex-row sm:items-start ${t.photoUrl ? "" : "justify-center"}`}>
            {t.photoUrl && (
              <div className="shrink-0">
                <div className="relative h-56 w-56 overflow-hidden sm:h-72 sm:w-72">
                  <Image
                    src={t.photoUrl}
                    alt={t.name}
                    fill
                    className="object-cover"
                    sizes="288px"
                  />
                </div>
              </div>
            )}
            <div className={`flex flex-col justify-center ${t.photoUrl ? "text-left" : "text-center"}`}>
              <blockquote className="text-lg leading-relaxed text-slate-700 font-[var(--font-display)] italic">
                &ldquo;{t.testimony}&rdquo;
              </blockquote>
              <div className="mt-6">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-900">
                  {t.name}
                </p>
                {t.itemPurchased && (
                  <p className="mt-1 text-xs uppercase tracking-[0.15em] text-slate-500">
                    {t.itemPurchased}
                  </p>
                )}
                {t.datePurchased && (
                  <p className="mt-1 text-xs text-slate-400">{t.datePurchased}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {testimonials.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-slate-400 transition hover:text-slate-700"
            aria-label="Previous testimonial"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-slate-400 transition hover:text-slate-700"
            aria-label="Next testimonial"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="mt-8 flex justify-center gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === current ? "w-6 bg-slate-700" : "w-1.5 bg-slate-300"
                }`}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
